import { Stack, CircularProgress, Grid, Button, TextField, Alert } from '@mui/material';
import Pagination from '@mui/material/Pagination';
import {
  Device,
  DeviceParameter,
  percentDeviceTypes,
  RefactoredDevice
} from '../../@types/model/device';
import { DeviceType } from '../../@types/model/user';
import { generatePath, NavLink, useNavigate } from 'react-router-dom';
import { PATH_DASHBOARD } from '../../routes/paths';
import SvgIconStyle from '../SvgIconStyle';
import * as React from 'react';
import { Fragment, useEffect, useState } from 'react';
import { RootState, useSelector } from '../../redux/store';
import BasicModal from '../BasicModal';
import Typography from '@mui/material/Typography';
import ShareWithUserForm from '../ShareWithUserForm';
import LoadingSpinner from '../LoadingSpinner';
import { CancelToken } from 'axios';
import axios from '../../utils/axios';
import DeviceGroupPicker from '../../../src/components/DeviceGroupPicker';
import DeviceProvincePicker from '../DeviceProvincePicker';
import WorkspacePicker from '../WorkspacePicker';
import DeviceStatePicker from '../DeviceStatePicker';
import FileSaver from 'file-saver';
import { useSnackbar } from 'notistack';
import LabelPicker from '../label/LabelPicker';
import { useTranslation } from 'react-i18next';
import LoadingSpinnerTransparent from 'components/LoadingSpinnerTransparent';

import { useTheme } from '@mui/material/styles';
import refactorDevices from './Components/refactorDevices';
import { checkModules, checkRoles, ROLE_END_USER, ROLE_USER } from 'guards/CheckRoles';
import '../../assets/css/gridTable.scss';
import DeviceToEditForm from 'components/DeviceToEditForm';
import GridInfo from 'components/GridInfo';
import { v4 } from 'uuid';
import { getProductTypes, productTypeState } from 'recoil/productTypeState';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { ProductType } from '../../@types/model/productType';
import GridImagePercent from './Components/gridImagePercent';
import { ProductSchedule, ProductScheduleItem } from '../../@types/model/productSchedule';
import shareDevice from './Components/shareDevice';

export const getDevices = async (
  page: number,
  size: number,
  okCallback: (
    deviceGroups: Device[],
    totalCount: number,
    productSchedules: ProductSchedule[],
    productTypes: ProductType[]
  ) => void,
  cancelable = true,
  filter: DeviceTableFilter | null = null,
  exportFormat: string | null = null
): Promise<void> => {
  if (cancel) {
    cancel.cancel();
  }
  if (cancelable) {
    // @ts-ignore
    cancel = CancelToken.source();
  }
  const queryParams: any = {
    offset: page * size,
    size
  };

  if (filter) {
    queryParams.order_by = filter.order_by;
    queryParams.direction = filter.direction;
  }
  if (filter && filter.deviceGroup) {
    queryParams.tags = [filter.deviceGroup];
  }
  if (filter && filter.tag) {
    queryParams.tags = [filter.tag];
  }

  if (filter && filter.workspace) {
    queryParams.workspace = filter.workspace;
  }

  if (filter && filter.state) {
    queryParams.state = filter.state;
  }

  if (filter && filter.text) {
    queryParams.text = filter.text;
  }
  if (exportFormat) {
    queryParams.format = exportFormat;
  }
  if (!exportFormat) {
    await axios
      .get('/api/v2/device', {
        cancelToken: cancelable ? cancel.token : null,
        params: queryParams
      })
      .then((data: any) => {
        if (data && data.data) {
          data = data.data;
          let productSchedules: ProductSchedule[] = [];
          let productTypes: ProductType[] = [];
          data.included.filter((item: any) => {
            if (
              item.type === 'App\\ProductSchedule\\Domain\\Model\\ProductSchedule\\ProductSchedule'
            )
              productSchedules.push(item.attributes as ProductSchedule);
            if (item.type === 'App\\ProductTypes\\Domain\\Model\\ProductType')
              productTypes.push(item.attributes as ProductType);
          });
          productSchedules.map((productSchedule: ProductSchedule) => {
            let schedules: ProductScheduleItem[] = [];
            Object.keys(productSchedule.schedule).forEach((key: any) => {
              schedules.push(productSchedule.schedule[key]);
            });
            productSchedule.schedule = schedules;
            return productSchedule;
          });
          okCallback(
            data.data.map((item: any) => {
              return item as Device;
            }),
            data.meta.total_count,
            productSchedules,
            productTypes
          );
        }
      });
  } else {
    axios
      .get('/api/v2/device', {
        cancelToken: cancelable ? cancel.token : null,
        params: queryParams,
        responseType: 'blob'
      })
      .then((response: any) => {
        okCallback([], 0, [], []);
        FileSaver.saveAs(response.data, response.headers.filename);
      });
  }
};

let cancel: any;

export type DeviceTableFilter = {
  text: string | undefined;
  deviceGroup: string | undefined;
  workspace: string | undefined;
  state: string;
  order_by: string;
  tag: string | undefined;
  direction: 'asc' | 'desc';
  province: string | undefined;
};

interface DeviceTableProps {
  onFilterChange: (filter: Device[]) => void | undefined;
  title: String;
  pagination: Boolean;
}

const filterDevices = (devices: Device[]) =>
  devices.filter((d: Device) => percentDeviceTypes.includes(d.attributes.device_type_preset));

const DeviceGridTable = (props: DeviceTableProps) => {
  const { workspace } = useSelector((state: RootState) => state.workspace);
  const [devices, setDevices] = useState<Device[]>([]);
  const [devicesProductSchedule, setDevicesProductSchedule] = useState<ProductSchedule[]>([]);
  const [devicesProductType, setDevicesProductType] = useState<ProductType[]>([]);
  const [refactoredDevices, setRefactoredDevices] = useState<RefactoredDevice[]>([]);
  const [totalDeviceCount, setTotalDeviceCount] = useState<number>(0);

  const [deviceToEdit, setDeviceToEdit] = useState<RefactoredDevice | null>(null);

  const [page, setPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(96);

  const { loaded, deviceTypes, user } = useSelector((state: RootState) => state.user);

  const [deviceToShare, setDeviceToShare] = useState<Device | null>(null);
  const [shareLoading, setShareLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [exportInProgress, setExportInProgress] = useState(false);

  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation(['translation', 'custom']);

  const [tableFilter, setTableFilter] = useState({
    deviceGroup: undefined,
    tag: undefined,
    order_by: 'identifier',
    state: 'any',
    direction: 'asc',
    text: undefined,
    province: undefined
  } as DeviceTableFilter);

  const theme = useTheme();

  const [loadingDevices, setLoadingDevices] = useState(false);
  const navigate = useNavigate();

  const productTypes: ProductType[] = useRecoilValue(productTypeState);
  const setProductTypes = useSetRecoilState(productTypeState);

  useEffect(() => {
    getProductTypes().then((productTypes: ProductType[]) => setProductTypes(productTypes));
  }, []);

  useEffect(() => {
    if (!props.pagination) setItemsPerPage(100);
    const fetchData = async () => {
      setLoadingDevices(true);
      await getDevices(
        page,
        itemsPerPage,
        (
          data: Device[],
          count: number,
          productSchedules: ProductSchedule[],
          productTypes: ProductType[]
        ) => {
          const filteredDevices = filterDevices(data);
          setDevices(filteredDevices);
          const refactoredDevices: RefactoredDevice[] = refactorDevices(filteredDevices);
          setRefactoredDevices(refactoredDevices);
          if (props.onFilterChange) {
            props.onFilterChange(data);
          }
          setTotalDeviceCount(count);
          setDevicesProductSchedule(productSchedules);
          setDevicesProductType(productTypes);
        },
        true,
        tableFilter
      );
      setLoadingDevices(false);
    };
    fetchData().catch(console.error);
  }, [page, itemsPerPage, tableFilter]);

  if (!loaded) {
    return <LoadingSpinner />;
  }

  const featuredDataNames: string[] = [];
  devices.forEach((d: Device) => {
    const deviceType = deviceTypes.find(
      (dt: DeviceType) => dt.guid === d.attributes.device_type_preset
    );
    if (deviceType) {
      deviceType.attributes.featured_data_names.forEach((dn: string) => {
        if (!featuredDataNames.includes(dn)) {
          featuredDataNames.push(dn);
        }
      });
    }
  });

  const handleChangePagination = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value - 1);
  };

  const editDevice = (
    device: RefactoredDevice,
    numero_de_crianza: string | null,
    tipo_de_producto: string | null,
    okCallback: () => void,
    koCallback: (error: any) => void
  ) => {
    let parametersNew: DeviceParameter[] = [];
    Object.keys(device.attributes.parameters).forEach((key: any) => {
      parametersNew.push(device.attributes.parameters[key]);
    });
    parametersNew = parametersNew.map((dp: DeviceParameter) => {
      return {
        guid: dp.guid,
        identifier: dp.identifier,
        value: dp.value ? dp.value : null
      };
    });

    if (numero_de_crianza) {
      let pf = parametersNew.filter((p) => {
        if (p.identifier === 'numero_de_crianza') return true;
        return false;
      });
      if (pf.length === 0) {
        parametersNew.push({
          guid: v4(),
          identifier: 'numero_de_crianza',
          value: numero_de_crianza
        });
      } else {
        parametersNew.map((p) => {
          if (p.identifier === 'numero_de_crianza') p.value = numero_de_crianza;
          return p;
        });
      }
    }

    if (tipo_de_producto) {
      let pf = parametersNew.filter((p) => {
        if (p.identifier === 'tipo_de_producto') return true;
        return false;
      });
      if (pf.length === 0) {
        parametersNew.push({
          guid: v4(),
          identifier: 'tipo_de_producto',
          value: tipo_de_producto
        });
      } else {
        parametersNew.map((p) => {
          if (p.identifier === 'tipo_de_producto') p.value = tipo_de_producto;
          return p;
        });
      }
    }

    axios
      .put(`/api/v2/device/` + device.guid, {
        name: device.attributes.name,
        state: device.attributes.state,
        tags: device.attributes.tags,
        last_location: device.attributes.last_location,
        parameters: parametersNew
      })
      .then(() => {
        setRefactoredDevices(
          refactoredDevices.map((rd: RefactoredDevice) => {
            if (rd.guid === device.guid) {
              return {
                ...rd,
                tipo_de_producto: tipo_de_producto,
                numero_de_crianza: numero_de_crianza
              };
            }
            return rd;
          })
        );
        okCallback();
      })
      .catch(koCallback);
  };

  return (
    <>
      <Fragment>
        {props.title !== '' && (
          <Stack
            direction={'row'}
            spacing={{ xs: 0, sm: 5 }}
            className="headerRow"
            sx={{
              [theme.breakpoints.up('sm')]: {
                flexDirection: 'row'
              }
            }}
          >
            <h2 style={{ marginBottom: '60px' }}>{props.title}</h2>
            {checkRoles(user, ROLE_END_USER) && checkModules(workspace, ['orders']) ? (
              <Button
                onClick={() => {
                  navigate(PATH_DASHBOARD.end_user.deliver.new);
                }}
                className="btn-request-delivery"
                variant={'contained'}
              >
                {t('request_delivery')}
              </Button>
            ) : null}
            <Button
              disabled={exportInProgress}
              sx={{
                color: '#637381',
                borderColor: '#637381',
                marginRight: '24px',
                marginTop: '50px',
                alignSelf: 'flex-end',
                width: '250px',
                height: '55px'
              }}
              variant={'outlined'}
              onClick={() => {
                setExportInProgress(true);
                getDevices(
                  0,
                  totalDeviceCount,
                  (
                    data: Device[],
                    count: number,
                    productSchedules: ProductSchedule[],
                    productTypes: ProductType[]
                  ) => {
                    setExportInProgress(false);
                  },
                  true,
                  tableFilter,
                  'xls'
                );
              }}
            >
              {exportInProgress ? (
                <CircularProgress size={30} />
              ) : (
                <SvgIconStyle
                  src="/static/icons/giraf/download.svg"
                  sx={{ width: '20px', height: '20px', display: 'block', marginRight: '10px' }}
                />
              )}
              {t('devices_export').toString()}
            </Button>
          </Stack>
        )}
        <Stack
          className="filter_container"
          direction={'row'}
          sx={{ width: '100%', padding: '10px' }}
        >
          <TextField
            sx={{ minWidth: '200px' }}
            type="text"
            value={tableFilter.text}
            label={t('search').toString()}
            onChange={(event) => setTableFilter({ ...tableFilter, text: event.target.value })}
          />
          <DeviceStatePicker
            selection={tableFilter.state}
            changeHandler={(state) => {
              setTableFilter({ ...tableFilter, state });
            }}
          />
          <DeviceGroupPicker
            deviceGroupGuid={tableFilter.deviceGroup}
            changeHandler={(deviceGroupGuid) => {
              if (deviceGroupGuid) {
                setTableFilter({ ...tableFilter, deviceGroup: deviceGroupGuid });
              } else {
                setTableFilter({ ...tableFilter, deviceGroup: undefined });
              }
            }}
            deviceTags={[]}
          />
          <WorkspacePicker
            workspaces={user!.attributes.workspaces}
            selectedWorkspace={tableFilter.workspace}
            changeHandler={(wks: string | null) => {
              if (wks) {
                setTableFilter({ ...tableFilter, workspace: wks });
              } else {
                setTableFilter({ ...tableFilter, workspace: undefined });
              }
            }}
          />
          <LabelPicker
            selection={tableFilter.tag}
            changeHandler={(tagGuid: string | undefined) => {
              setTableFilter({ ...tableFilter, tag: tagGuid });
            }}
          />
          <DeviceProvincePicker
            deviceProvince={tableFilter.province}
            changeHandler={(deviceProvince: any) => {
              if (deviceProvince) setTableFilter({ ...tableFilter, province: deviceProvince });
              else setTableFilter({ ...tableFilter, province: undefined });
            }}
          />
        </Stack>
        <Grid container spacing={2} style={styles.grid}>
          {refactoredDevices.map((rd: RefactoredDevice, index) => {
            const identifier = rd.attributes.identifier;
            //We have to search if it exist a productSchedule that is using this device. And get the product related
            let productNow: ProductType | null = null;
            let productScheduleNow: ProductSchedule | null = null;
            let productScheduleFilter: ProductSchedule[] = devicesProductSchedule.filter(
              (ps: ProductSchedule) => ps.devices.includes(rd.guid) && ps.state === 'active'
            );
            if (productScheduleFilter.length > 0) {
              productScheduleNow = productScheduleFilter[0];
              // There ProductSchedule that is using this device. We should get the device.
              let current_item: number = productScheduleFilter[0].currentItem;
              if (current_item === 0) current_item = 1;
              let schedules = productScheduleFilter[0].schedule?.filter(
                (psi: ProductScheduleItem) => {
                  return psi.ordinal === current_item;
                }
              );
              if (schedules.length > 0) {
                let dpts = devicesProductType.filter(
                  (spt: ProductType) => spt.guid === schedules[0].product
                );
                if (dpts.length > 0) productNow = dpts[0];
              }
            }
            return (
              <Grid key={index} item xs={12} sm={6} md={4}>
                <Grid container spacing={0} style={styles.item}>
                  <Grid item xs={3} className="itemImage">
                    <GridImagePercent
                      rd={rd}
                      user={user}
                      setDeviceToShare={(rd: RefactoredDevice) => {
                        setDeviceToShare(rd);
                      }}
                    ></GridImagePercent>
                  </Grid>
                  <Grid item xs={9} className="grid1">
                    <h4>
                      <NavLink
                        to={
                          checkRoles(user, ROLE_USER)
                            ? generatePath(PATH_DASHBOARD.devices.details, { guid: rd.guid })
                            : generatePath(PATH_DASHBOARD.end_user.devices.details, {
                                guid: rd.guid
                              })
                        }
                        style={{
                          textDecoration: 'none'
                        }}
                      >
                        {rd.attributes.name ?? identifier}
                      </NavLink>
                    </h4>
                    {/* <hr className="hr-whitesmoke" /> */}
                    <div style={{ display: 'flex' }}>
                      <div
                        style={{
                          paddingRight: '10px',
                          maxWidth:
                            rd.attributes.device_type_preset === 'preset:metrosil-v1'
                              ? '50%'
                              : 'inherit'
                        }}
                      >
                        {/* <p>
                          {t('last_temperature')}: {rd.temperature} Cº
                        </p> */}
                        <GridInfo
                          label={t('identifier')}
                          value={identifier}
                          edit={false}
                          onEdit={() => {}}
                        ></GridInfo>
                        {rd.capacidad !== 0 && (
                          <GridInfo
                            label={t('capacity')}
                            value={rd.capacidad + rd.unit}
                            edit={false}
                            onEdit={() => {}}
                          ></GridInfo>
                        )}
                        {rd.capacidad !== 0 && (
                          <GridInfo
                            label={t('aprox_unit', { ns: 'custom' })}
                            value={
                              (rd.volumen_aproximado
                                ? Math.round(rd.volumen_aproximado)
                                : Math.round((rd.percent * rd.capacidad) / 100)) +
                              '' +
                              rd.unit
                            }
                            edit={false}
                            onEdit={() => {}}
                          ></GridInfo>
                        )}
                        <GridInfo
                          label={t('daily_consum')}
                          value={'-'}
                          edit={false}
                          onEdit={() => {}}
                        ></GridInfo>
                        <GridInfo
                          label={t('weekly_consum')}
                          value={'-'}
                          edit={false}
                          onEdit={() => {}}
                        ></GridInfo>
                      </div>
                      {rd.attributes.device_type_preset === 'preset:metrosil-v1' ? (
                        <div className="gridTableEditMetrosil">
                          <div className="gridTableEditMetrosil--params">
                            <GridInfo
                              label={t('feed_type')}
                              value={productNow?.name ?? null}
                              edit={false}
                              onEdit={() => {
                                setDeviceToEdit(rd);
                              }}
                            ></GridInfo>
                            <GridInfo
                              label={t('breeding_number')}
                              value={productScheduleNow?.reference ?? null}
                              edit={false}
                              onEdit={() => {}}
                            ></GridInfo>
                          </div>
                          {rd.calibrado === false ? (
                            <Alert
                              className="gridTableEditMetrosil--calibrate"
                              variant="filled"
                              severity="error"
                            >
                              {t('pending_calibration')}
                            </Alert>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                  </Grid>
                </Grid>
              </Grid>
            );
          })}
          {loadingDevices && (
            <div className="loadingDeviceSpinner">
              <LoadingSpinnerTransparent />
            </div>
          )}
        </Grid>
        {props.pagination && (
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Pagination
                count={Math.ceil(totalDeviceCount / itemsPerPage)}
                variant="outlined"
                shape="rounded"
                style={{
                  marginTop: 20
                }}
                onChange={handleChangePagination}
              />
            </Grid>
          </Grid>
        )}
        <BasicModal
          open={deviceToShare !== null}
          handleClose={() => {
            setDeviceToShare(null);
          }}
        >
          {deviceToShare ? (
            <Fragment>
              <Typography id="modal-modal-title" variant="h6" component="h2">
                {t('share')} {deviceToShare.attributes.name} ({deviceToShare.attributes.identifier})
              </Typography>
              <ShareWithUserForm
                submitHander={(name: string, email: string) => {
                  setShareLoading(true);
                  shareDevice(
                    deviceToShare.guid,
                    name,
                    email,
                    () => {
                      setShareLoading(false);
                      setDeviceToShare(null);
                      enqueueSnackbar(t('device_shared_successfuly'), { variant: 'success' });
                    },
                    (error) => {
                      setShareLoading(false);
                      enqueueSnackbar(error.message, { variant: 'error' });
                    }
                  );
                }}
                loading={shareLoading}
              />
            </Fragment>
          ) : null}
        </BasicModal>
        <BasicModal
          open={deviceToEdit !== null}
          handleClose={() => {
            setDeviceToEdit(null);
          }}
        >
          {deviceToEdit ? (
            <Fragment>
              <Typography id="modal-modal-title" variant="h6" component="h2">
                {t('Edit')} {deviceToEdit.attributes.name}
              </Typography>
              <DeviceToEditForm
                refactoredDevice={deviceToEdit}
                submitHander={(
                  tipo_de_producto: string | null,
                  numero_de_crianza: string | null
                ) => {
                  setEditLoading(true);
                  editDevice(
                    deviceToEdit,
                    numero_de_crianza,
                    tipo_de_producto,
                    () => {
                      setEditLoading(false);
                      setDeviceToEdit(null);
                      enqueueSnackbar(t('device_edit_successfuly'), { variant: 'success' });
                    },
                    (error) => {
                      setEditLoading(false);
                      enqueueSnackbar(error.message, { variant: 'error' });
                    }
                  );
                }}
                loading={editLoading}
              />
            </Fragment>
          ) : null}
        </BasicModal>
      </Fragment>
    </>
  );
};

const styles = {
  grid: {
    position: 'relative' as 'relative',
    padding: 10
  },
  item: {
    background: '#ececec',
    borderRadius: 20
  }
};

export default DeviceGridTable;
